import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Bell, Plus, RefreshCw, Activity,
  Target, ShieldAlert, X, Trash2, Sliders, Mail, CheckCircle2, AlertTriangle,
  ChevronRight, Search as SearchIcon, Settings as SettingsIcon, Newspaper, ExternalLink,
  CalendarClock, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import StockDetail from "@/components/StockDetail";
import SettingsDialog from "@/components/SettingsDialog";
import BrandLogo from "@/components/BrandLogo";

const STATUS_META = {
  confirmed_breakout: { label: "CONFIRMED BO", color: "text-emerald-400", bg: "bg-emerald-500/15", border: "border-emerald-500/50", dot: "bg-emerald-500" },
  broken_out: { label: "BROKEN OUT", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", dot: "bg-emerald-500" },
  testing_breakout: { label: "TESTING BO", color: "text-yellow-300", bg: "bg-yellow-500/10", border: "border-yellow-500/40", dot: "bg-yellow-400" },
  weak_breakout: { label: "WEAK BO ⚠", color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/40", dot: "bg-orange-500" },
  failed_breakout: { label: "FAILED BO", color: "text-slate-400", bg: "bg-slate-700/40", border: "border-slate-500/40", dot: "bg-slate-500" },
  approaching_breakout: { label: "APPROACHING", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", dot: "bg-amber-500" },
  breakdown: { label: "BREAKDOWN", color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30", dot: "bg-rose-500" },
  approaching_support: { label: "AT SUPPORT", color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30", dot: "bg-rose-400" },
  consolidating: { label: "CONSOLIDATING", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30", dot: "bg-blue-500" },
  error: { label: "ERROR", color: "text-slate-400", bg: "bg-slate-700/30", border: "border-slate-600/40", dot: "bg-slate-500" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "confirmed_breakout", label: "Confirmed" },
  { id: "approaching_breakout", label: "Approaching" },
  { id: "weak_breakout", label: "Weak / Risk" },
  { id: "breakdown", label: "Breakdown" },
  { id: "consolidating", label: "Consolidating" },
];

function Sparkline({ data, status }) {
  if (!data || data.length < 2) return <div className="h-10 w-full bg-slate-800/50 rounded" />;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const w = 120, h = 36;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const color = data[data.length-1] > data[0] ? "#10B981" : "#F43F5E";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
    </svg>
  );
}

function StockCard({ item, onOpen, onDelete, prevPrice }) {
  const q = item.quote;
  const status = STATUS_META[item.status] || STATUS_META.consolidating;
  const flashRef = useRef(null);
  useEffect(() => {
    if (!q || prevPrice == null || !flashRef.current) return;
    if (q.price > prevPrice) {
      flashRef.current.classList.add("flash-up");
      setTimeout(() => flashRef.current?.classList.remove("flash-up"), 1000);
    } else if (q.price < prevPrice) {
      flashRef.current.classList.add("flash-down");
      setTimeout(() => flashRef.current?.classList.remove("flash-down"), 1000);
    }
  }, [q?.price, prevPrice]);

  if (item.error || !q) {
    return (
      <div className="rounded-xl border border-slate-800 bg-[#121824] p-4" data-testid={`stock-card-${item.symbol}`}>
        <div className="flex justify-between items-center">
          <div>
            <div className="font-head font-bold text-base">{item.symbol}</div>
            <div className="text-xs text-slate-500 mt-1">{item.error || "No data"}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onDelete(item)} data-testid={`delete-stock-${item.symbol}`}>
            <Trash2 className="w-4 h-4 text-slate-500" />
          </Button>
        </div>
      </div>
    );
  }

  const up = q.change >= 0;
  const volRatio = item.volume_ratio || 0;
  const volBarPct = Math.min(volRatio * 50, 100);

  return (
    <div
      ref={flashRef}
      onClick={() => onOpen(item)}
      className="group rounded-xl border border-slate-800 hover:border-slate-700 bg-[#121824] p-4 cursor-pointer transition-colors"
      data-testid={`stock-card-${item.symbol}`}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-head font-bold text-lg tracking-tight">{item.symbol}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold border ${status.bg} ${status.border} ${status.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
            {typeof item.earnings_days === "number" && item.earnings_days >= 0 && item.earnings_days <= 7 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-300" title={`Earnings ${item.earnings_date}`} data-testid={`earnings-badge-${item.symbol}`}>
                <CalendarClock className="w-2.5 h-2.5" />
                EARNINGS {item.earnings_days === 0 ? "TODAY" : `IN ${item.earnings_days}D`}
              </span>
            )}
          </div>
          {item.name && <div className="text-xs text-slate-500 truncate mt-0.5">{item.name}</div>}
        </div>
        <div className="text-right">
          <div className="font-mono font-bold text-lg leading-tight">${q.price.toFixed(2)}</div>
          <div className={`font-mono text-xs ${up ? "text-emerald-400" : "text-rose-400"}`}>
            {up ? "+" : ""}{q.change.toFixed(2)} ({up ? "+" : ""}{q.change_percent.toFixed(2)}%)
          </div>
        </div>
      </div>

      <div className="mt-3 h-10"><Sparkline data={item.sparkline} status={item.status} /></div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] font-mono">
        <div className="border-l-2 border-emerald-500/60 pl-2">
          <div className="text-slate-500 uppercase tracking-wider text-[9px]">Breakout</div>
          <div className="text-emerald-400 font-semibold">${item.breakout_level?.toFixed(2) || "—"}</div>
          <div className="text-slate-500">{item.distance_to_breakout_pct > 0 ? `${item.distance_to_breakout_pct.toFixed(2)}% away` : `+${Math.abs(item.distance_to_breakout_pct).toFixed(2)}% past`}</div>
        </div>
        <div className="border-l-2 border-rose-500/60 pl-2">
          <div className="text-slate-500 uppercase tracking-wider text-[9px]">Support</div>
          <div className="text-rose-400 font-semibold">${item.support_level?.toFixed(2) || "—"}</div>
          <div className="text-slate-500">{item.distance_to_support_pct > 0 ? `${item.distance_to_support_pct.toFixed(2)}% above` : `${Math.abs(item.distance_to_support_pct).toFixed(2)}% below`}</div>
        </div>
      </div>

      {(item.target_1 || item.target_2 || item.target_3) && (
        <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] font-mono" data-testid={`targets-${item.symbol}`}>
          {[1,2,3].map(n => {
            const v = item[`target_${n}`];
            const hit = item.target_hit >= n;
            return (
              <div key={n} className={`rounded-md border px-2 py-1 ${hit ? "bg-amber-500/15 border-amber-500/50 text-amber-300" : "border-slate-800 text-slate-400"}`}>
                <div className="text-[8px] uppercase tracking-wider opacity-70">T{n}{hit ? " · HIT" : ""}</div>
                <div className="font-semibold">${v ? v.toFixed(2) : "—"}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3">
        <div className="flex justify-between items-center text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1">
          <span title="Volume on the last daily close vs 20-day average. ≥1.5× = confirmed, 1.0–1.5× = mild, <1.0× = fakeout risk.">
            Vol Confirmation {item.last_vol_ratio != null && `· ${item.last_vol_ratio.toFixed(2)}x`}
          </span>
          <span
            className={`font-semibold ${
              item.vol_confirm === "confirmed" ? "text-emerald-400"
              : item.vol_confirm === "mild" ? "text-yellow-400"
              : "text-rose-400"
            }`}
          >
            {item.vol_confirm === "confirmed" ? "● CONFIRMED"
             : item.vol_confirm === "mild" ? "● MILD"
             : "● WEAK · fakeout risk"}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className={`h-full ${item.vol_confirm === "confirmed" ? "bg-emerald-500" : item.vol_confirm === "mild" ? "bg-yellow-500" : "bg-rose-500"}`}
            style={{ width: `${Math.min((item.last_vol_ratio || 0) * 50, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function AddStockDialog({ open, onOpenChange, onAdded }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [notes, setNotes] = useState("");

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/stocks/${query.trim().toUpperCase()}/search`);
      setResults(data || []);
    } catch (e) { toast.error("Search failed"); }
    setSearching(false);
  };

  const addQuick = async (symbol, name) => {
    setAdding(true);
    try {
      await api.post("/watchlist", { symbol, name, email_alerts: emailAlerts, notes });
      toast.success(`${symbol} added to watchlist`);
      setQuery(""); setResults([]); setNotes("");
      onAdded();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
    setAdding(false);
  };

  const addDirect = async () => {
    if (!query.trim()) return;
    await addQuick(query.trim().toUpperCase(), null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-800 text-slate-100 max-w-md" data-testid="add-stock-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">Add Stock to Watchlist</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Breakout/support levels auto-calculated from 20-day high/low. You can override anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Ticker (e.g. AAPL, NVDA)"
              className="bg-[#0B0F17] border-slate-800 font-mono"
              data-testid="search-ticker-input"
            />
            <Button onClick={search} disabled={searching} variant="secondary" data-testid="search-ticker-btn">
              <SearchIcon className="w-4 h-4" />
            </Button>
          </div>
          {results.length > 0 && (
            <div className="max-h-48 overflow-y-auto border border-slate-800 rounded-md divide-y divide-slate-800">
              {results.map((r) => (
                <button
                  key={r.symbol}
                  onClick={() => addQuick(r.symbol, r.name)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-800/50 flex justify-between items-center"
                  data-testid={`result-${r.symbol}`}
                >
                  <div>
                    <div className="font-mono font-semibold text-sm">{r.symbol}</div>
                    <div className="text-[11px] text-slate-500 truncate max-w-[240px]">{r.name}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </button>
              ))}
            </div>
          )}
          <Textarea
            placeholder="Notes / setup thesis (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-[#0B0F17] border-slate-800 text-sm min-h-[60px]"
            data-testid="notes-input"
          />
          <div className="flex items-center justify-between py-1">
            <Label className="text-sm text-slate-300 flex items-center gap-2"><Mail className="w-4 h-4" /> Email alerts</Label>
            <Switch checked={emailAlerts} onCheckedChange={setEmailAlerts} data-testid="email-alerts-switch" />
          </div>
          <Button
            onClick={addDirect}
            disabled={!query.trim() || adding}
            className="w-full bg-indigo-500 hover:bg-indigo-600"
            data-testid="confirm-add-stock"
          >
            <Plus className="w-4 h-4 mr-1" /> Add {query || "Stock"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AlertsSheet({ open, onOpenChange, alerts, refresh }) {
  const markAllRead = async () => { await api.post("/alerts/read-all"); refresh(); };
  const del = async (id) => { await api.delete(`/alerts/${id}`); refresh(); };
  const sendDigest = async () => {
    toast("Building digest…");
    try {
      const { data } = await api.post("/digest/preview");
      toast.success(`Digest sent (${data.bullish ?? 0} bullish, ${data.bearish ?? 0} bearish, ${data.earnings ?? 0} earnings)`);
    } catch (e) { toast.error("Digest failed"); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#121824] border-slate-800 text-slate-100 w-full sm:max-w-md" data-testid="alerts-sheet">
        <SheetHeader>
          <SheetTitle className="font-head text-xl flex items-center gap-2 text-slate-100">
            <Bell className="w-5 h-5" /> Alerts
          </SheetTitle>
        </SheetHeader>
        <div className="mt-3 flex justify-between items-center gap-2">
          <Button variant="ghost" size="sm" onClick={sendDigest} className="text-xs text-indigo-400 hover:text-indigo-300" data-testid="send-digest-btn">
            <Send className="w-3 h-3 mr-1" /> Send digest now
          </Button>
          <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs" data-testid="mark-all-read">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Mark all read
          </Button>
        </div>
        <div className="mt-2 space-y-2 overflow-y-auto max-h-[calc(100vh-160px)] pr-1">
          {alerts.length === 0 && (
            <div className="text-center text-slate-500 py-10 text-sm font-mono">No alerts yet</div>
          )}
          {alerts.map((a) => {
            const isBreakout = a.kind === "breakout";
            const isBreakdown = a.kind === "breakdown";
            const isTarget = a.kind?.startsWith("target_");
            const isNews = a.kind?.startsWith("news_");
            const isNewsBull = a.kind === "news_bullish";
            const Icon = isNews ? Newspaper : isBreakout ? TrendingUp : isBreakdown ? TrendingDown : isTarget ? Target : AlertTriangle;
            const color = isNewsBull ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
                        : isNews ? "text-rose-400 border-rose-500/30 bg-rose-500/5"
                        : isBreakout ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
                        : isBreakdown ? "text-rose-400 border-rose-500/30 bg-rose-500/5"
                        : isTarget ? "text-amber-300 border-amber-500/30 bg-amber-500/5"
                        : "text-amber-400 border-amber-500/30 bg-amber-500/5";
            return (
              <div key={a.id} className={`rounded-lg border p-3 ${color}`} data-testid={`alert-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-mono font-semibold text-sm">{a.symbol}</div>
                      <div className="text-xs text-slate-300 mt-0.5 break-words">{a.message}</div>
                      <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2 font-mono">
                        {new Date(a.created_at).toLocaleString()}
                        {a.emailed && <span className="inline-flex items-center gap-1"><Mail className="w-2.5 h-2.5" />sent</span>}
                        {isNews && a.news_url && (
                          <a href={a.news_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-indigo-400 hover:text-indigo-300" data-testid={`news-link-${a.id}`}>
                            Read <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => del(a.id)} className="text-slate-500 hover:text-slate-300" data-testid={`del-alert-${a.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TickerBar({ items }) {
  const strip = items.filter(i => i.quote).slice(0, 20);
  if (strip.length === 0) return null;
  const double = [...strip, ...strip];
  return (
    <div className="border-y border-slate-800 bg-[#0B0F17] overflow-hidden">
      <div className="marquee-track py-2">
        {double.map((it, i) => {
          const up = it.quote.change >= 0;
          return (
            <div key={`${it.symbol}-${i}`} className="flex items-center gap-2 px-5 border-r border-slate-800/60 font-mono text-xs whitespace-nowrap">
              <span className="font-semibold">{it.symbol}</span>
              <span>${it.quote.price.toFixed(2)}</span>
              <span className={up ? "text-emerald-400" : "text-rose-400"}>
                {up ? "+" : ""}{it.quote.change_percent.toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [filter, setFilter] = useState("all");
  const [countdown, setCountdown] = useState(60);
  const prevPrices = useRef({});
  const [config, setConfig] = useState({ owner_email: "", refresh_interval_s: 60, alpha_key_masked: "", brand: null });
  const loadConfig = useCallback(() => api.get("/config").then(r => {
    setConfig(r.data);
    if (r.data?.brand?.name) document.title = `${r.data.brand.name} · Stock analyzer`;
  }).catch(() => {}), []);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: dash }, { data: alertsData }] = await Promise.all([
        api.get("/dashboard"),
        api.get("/alerts"),
      ]);
      // detect new alerts
      const prevAlertIds = new Set(alerts.map(a => a.id));
      const newOnes = (alertsData || []).filter(a => !prevAlertIds.has(a.id));
      newOnes.forEach(a => {
        if (a.kind === "confirmed_breakout") toast.success(`✅ ${a.symbol} CONFIRMED breakout @ $${a.price.toFixed(2)}`, { duration: 10000 });
        else if (a.kind === "weak_breakout") toast(`⚠ ${a.symbol} weak breakout — fakeout risk`, { duration: 8000 });
        else if (a.kind === "failed_breakout") toast(`${a.symbol} failed breakout — back below level`);
        else if (a.kind === "testing_breakout") toast(`${a.symbol} testing level — wait for close`, { duration: 5000 });
        else if (a.kind === "breakout") toast.success(`${a.symbol} BROKE OUT @ $${a.price.toFixed(2)}`);
        else if (a.kind === "breakdown") toast.error(`${a.symbol} BROKE DOWN @ $${a.price.toFixed(2)}`);
        else if (a.kind?.startsWith("target_")) {
          const n = a.kind.split("_")[1];
          toast(`🎯 ${a.symbol} hit sell target ${n} @ $${a.price.toFixed(2)}`, { duration: 8000 });
        }
        else if (a.kind === "news_bullish") toast.success(`📈 ${a.symbol} bullish news: ${a.message.replace(/^BULLISH news: /, "").slice(0, 60)}`);
        else if (a.kind === "news_bearish") toast.error(`📉 ${a.symbol} bearish news: ${a.message.replace(/^BEARISH news: /, "").slice(0, 60)}`);
        else toast(`${a.symbol}: ${a.message}`);
      });
      // store prev prices
      items.forEach(i => { if (i.quote) prevPrices.current[i.symbol] = i.quote.price; });
      setItems(dash.items || []);
      setAlerts(alertsData || []);
      setUpdatedAt(dash.updated_at);
      setCountdown(60);
    } catch (e) {
      if (!silent) toast.error("Failed to load dashboard");
    }
    setLoading(false);
  }, [items, alerts]);

  // Initial load + config
  useEffect(() => {
    api.get("/config").then(r => {
      setConfig(r.data);
      if (r.data?.brand?.name) document.title = `${r.data.brand.name} · Stock analyzer`;
    }).catch(() => {});
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 60000);
    const tick = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => { clearInterval(interval); clearInterval(tick); };
  }, [fetchData]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "approaching_breakout") {
      return items.filter(i => i.status === "approaching_breakout");
    }
    return items.filter(i => i.status === filter);
  }, [items, filter]);

  const unreadCount = alerts.filter(a => !a.read).length;
  const counts = useMemo(() => {
    const c = { all: items.length, confirmed_breakout: 0, approaching_breakout: 0, weak_breakout: 0, breakdown: 0, consolidating: 0 };
    items.forEach(i => { if (c[i.status] !== undefined) c[i.status]++; });
    return c;
  }, [items]);

  const deleteItem = async (item) => {
    await api.delete(`/watchlist/${item.id}`);
    toast.success(`${item.symbol} removed`);
    fetchData(true);
  };

  return (
    <div className="grain min-h-screen relative">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F17]/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border"
              style={{ background: `${config.brand?.color || "#6366F1"}18`, borderColor: `${config.brand?.color || "#6366F1"}55` }}
            >
              <BrandLogo color={config.brand?.color || "#6366F1"} size={26} />
            </div>
            <div className="min-w-0">
              <div className="font-head font-extrabold text-base sm:text-lg tracking-tight leading-none" data-testid="brand-name">
                {config.brand?.name || "LaukStocks"}
              </div>
              <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest leading-none mt-0.5 hidden sm:block max-w-[320px] truncate" data-testid="brand-tagline">
                {config.brand?.tagline || "Live levels · news · alerts"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 font-mono text-[10px] text-slate-400 px-2 py-1 rounded-md border border-slate-800">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
              LIVE · {countdown}s
            </div>
            <Link to="/about" className="hidden md:inline-flex items-center font-mono text-[10px] text-slate-400 hover:text-slate-100 uppercase tracking-widest px-2 py-1" data-testid="about-link">
              About
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)} data-testid="settings-btn" className="text-slate-400 hover:text-slate-100">
              <SettingsIcon className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => fetchData()} data-testid="refresh-btn" className="text-slate-400 hover:text-slate-100">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost" size="icon"
              onClick={() => setAlertsOpen(true)}
              data-testid="alerts-btn"
              className="relative text-slate-400 hover:text-slate-100"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose-500 text-[9px] font-mono font-bold flex items-center justify-center text-white">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button onClick={() => setAddOpen(true)} size="sm" className="h-8 text-white hover:opacity-90" style={{ backgroundColor: config.brand?.color || "#6366F1" }} data-testid="add-stock-btn">
              <Plus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Add Stock</span>
            </Button>
          </div>
        </div>
      </header>

      <TickerBar items={items} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 relative z-10">
        {/* summary bento */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: "Watching", value: counts.all, color: "text-slate-300", icon: Activity },
            { label: "Confirmed BO", value: counts.confirmed_breakout, color: "text-emerald-400", icon: TrendingUp },
            { label: "Approaching", value: counts.approaching_breakout, color: "text-amber-400", icon: Target },
            { label: "Breakdown", value: counts.breakdown, color: "text-rose-400", icon: ShieldAlert },
          ].map(s => (
            <div key={s.label} className="rounded-xl border border-slate-800 bg-[#121824] p-3.5" data-testid={`stat-${s.label.toLowerCase().replace(" ","-")}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">{s.label}</span>
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
              </div>
              <div className={`font-mono font-bold text-2xl mt-1 ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full font-mono text-xs uppercase tracking-wider border transition-colors ${
                filter === f.id
                  ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-300"
                  : "bg-transparent border-slate-800 text-slate-400 hover:border-slate-700"
              }`}
            >
              {f.label} {f.id !== "all" && counts[f.id] !== undefined && <span className="ml-1 opacity-60">{counts[f.id]}</span>}
            </button>
          ))}
        </div>

        {/* Cards grid */}
        {loading && items.length === 0 ? (
          <div className="text-center py-20 text-slate-500 font-mono text-sm">Loading market data…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-800 rounded-xl">
            <div className="font-mono text-sm text-slate-500 mb-3">
              {items.length === 0 ? "Your watchlist is empty" : `No stocks in "${filter}"`}
            </div>
            {items.length === 0 && (
              <Button onClick={() => setAddOpen(true)} className="bg-indigo-500 hover:bg-indigo-600" data-testid="empty-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add your first stock
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stocks-grid">
            {filtered.map(item => (
              <StockCard
                key={item.id}
                item={item}
                prevPrice={prevPrices.current[item.symbol]}
                onOpen={setDetailItem}
                onDelete={deleteItem}
              />
            ))}
          </div>
        )}

        {updatedAt && (
          <div className="mt-6 text-center font-mono text-[10px] text-slate-600 uppercase tracking-widest">
            Last update: {new Date(updatedAt).toLocaleTimeString()} · Alerts to {config.owner_email || "none"}
          </div>
        )}

        {config.brand?.disclaimer && (
          <div className="mt-4 mb-2 text-center font-mono text-[10px] text-slate-600 italic max-w-2xl mx-auto leading-relaxed" data-testid="brand-disclaimer">
            {config.brand.disclaimer}
          </div>
        )}
      </main>

      <AddStockDialog open={addOpen} onOpenChange={setAddOpen} onAdded={() => fetchData(true)} />
      <AlertsSheet open={alertsOpen} onOpenChange={setAlertsOpen} alerts={alerts} refresh={() => fetchData(true)} />
      <StockDetail item={detailItem} onClose={() => setDetailItem(null)} onUpdated={() => fetchData(true)} onDelete={deleteItem} />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} config={config} refreshConfig={loadConfig} />
    </div>
  );
}
